import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { fileAccepted, fileBundleMatchSize, fileBundleMaxFileCount, fileMatchSize, filenameIllegalCharacter, filenameMaximumSize, } from '@zurich/common/forms';
import { useTranslation } from '@zurich/common/hooks';
import { fileUploadSelectorFamily } from '@zurich/common/recoil';
import { styled } from '../../utils';
import { OverridableButton } from '../Button';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
export function FileInput({ onChange, id, accept = 'image/jpeg,image/jpg,image/png,application/pdf,image/bmp,image/tiff', disabled, required, error, helperText, maxSize = 15, maxBundleSize = 100, maxCharacterSize = 255, maxFileCount, autocomplete = 'off', multiple = true, fileInputRef, buttonSize = 'large', buttonPaddingTop = '0px', buttonText, acceptedFileNameRegex, ...props }) {
    const { t } = useTranslation();
    const adjustedButtonText = buttonText ?? t('common:button_label.file_upload');
    const uploadedFiles = useRecoilValue(fileUploadSelectorFamily(id));
    const handleChange = useCallback((event) => {
        const { files } = event.target;
        const filesAccepted = [];
        const filesRejected = [];
        const bundleRejected = [];
        if (!files) {
            onChange?.(filesAccepted, filesRejected, bundleRejected, event);
            return;
        }
        const { bundleSizeError } = fileBundleMatchSize([...(uploadedFiles?.map((fObject) => fObject.file) ?? []), ...files], maxBundleSize, t);
        if (bundleSizeError) {
            bundleRejected.push(bundleSizeError);
        }
        const { fileCountError } = fileBundleMaxFileCount([...files], maxFileCount, t);
        if (fileCountError) {
            bundleRejected.push(fileCountError);
        }
        [...files].forEach((file) => {
            const { accepted, acceptError } = fileAccepted(file, accept || '', t);
            const { sizeMatch, sizeError } = fileMatchSize(file, maxSize, t);
            const { filenameMatch, filenameError } = filenameIllegalCharacter(file, acceptedFileNameRegex, t);
            const { filenameSizeMatch, filenameSizeError } = filenameMaximumSize(file, maxCharacterSize, t);
            if (accepted && sizeMatch && filenameMatch && filenameSizeMatch) {
                filesAccepted.push({ file, errors: [] });
            }
            else {
                filesRejected.push({
                    file,
                    errors: [
                        acceptError,
                        sizeError,
                        filenameError,
                        filenameSizeError,
                    ].filter((e) => e),
                });
            }
            if (onChange) {
                onChange(filesAccepted, filesRejected, bundleRejected, event);
            }
        });
    }, [
        uploadedFiles,
        maxBundleSize,
        t,
        maxFileCount,
        onChange,
        accept,
        maxSize,
        acceptedFileNameRegex,
        maxCharacterSize,
    ]);
    // FIXME: We handle file specific errors in utils. It will be better that handle this kind errors in yup schemna
    const disableText = !helperText?.split('.').includes('fileTypeError');
    return (_jsxs(DivStyled, { ref: fileInputRef, buttonPaddingTop: buttonPaddingTop, children: [_jsx("input", { ...props, type: "file", id: id, multiple: multiple, autoComplete: autocomplete, onChange: handleChange, hidden: true, accept: accept }), _jsxs(OverridableButton, { component: "label", htmlFor: id, variant: "text", size: buttonSize, ...(error && {
                    color: 'error',
                }), disabled: disabled, startIcon: _jsx(Icon, { ...(error &&
                        !disabled && {
                        color: 'error',
                    }), fontSize: "medium", variant: "form-add" }), children: [adjustedButtonText, " ", required ? ' *' : ''] }), error && disableText && (_jsx(Typography, { color: "error", variant: "body2", children: helperText }))] }));
}
const DivStyled = styled('div')(({ buttonPaddingTop }) => ({
    whiteSpace: 'nowrap',
    paddingTop: buttonPaddingTop,
}));
